import * as React from "react"
import { useEffect, useRef, useState } from "react"
import { Modal, Button } from "react-bootstrap"
import GoogleMapReact from "google-map-react"
import Settings from "../../common/Settings"
import "./GeoPickerModal.css"
import LeafletGeoPickerMap from "./LeafletGeoPickerMap"
import { useVisitsPreferences } from "../../hooks/useLocalStorage"

interface GeoPickerModalProps {
    show: boolean
    onClose: () => void
    onSelect?: (pos: { lat: number; lng: number }) => void
    initialPosition?: { lat: number; lng: number }
}

type MapPosition = { lat: number; lng: number }

interface MapMarker {
    setPosition: (pos: MapPosition) => void
}

interface GoogleMapLike {
    panTo: (pos: MapPosition) => void
}

interface GoogleMapsLike {
    Marker: new (args: { position: MapPosition; map: GoogleMapLike }) => MapMarker
}

const GeoPickerModal: React.FC<GeoPickerModalProps> = ({
    show,
    onClose,
    onSelect,
    initialPosition,
}) => {
    const [position, setPosition] = useState<MapPosition | null>(null)
    const { mapProvider } = useVisitsPreferences()
    const mapRef = useRef<GoogleMapLike | null>(null)
    const mapsRef = useRef<GoogleMapsLike | null>(null)
    const markerRef = useRef<MapMarker | null>(null)

    useEffect(() => {
        if (!show) {
            setPosition(null)
            return
        }
        if (position) return
        if (initialPosition?.lat && initialPosition?.lng) {
            setPosition(initialPosition)
        } else {
            setPosition({ lat: 18.4718609, lng: -69.8923187 })
        }
    }, [show])

    const handleMapClick = ({ lat, lng }: MapPosition) => {
        setPosition({ lat, lng })
    }

    const handleApiLoaded = ({ map, maps }: { map: GoogleMapLike; maps: GoogleMapsLike }) => {
        mapRef.current = map
        mapsRef.current = maps
        if (position && !markerRef.current) {
            markerRef.current = new maps.Marker({ position, map })
        }
    }

    useEffect(() => {
        if (!position || !mapRef.current || !mapsRef.current) return
        if (!markerRef.current && mapsRef.current) {
            markerRef.current = new mapsRef.current.Marker({
                position,
                map: mapRef.current,
            })
        } else {
            markerRef.current.setPosition(position)
        }
        mapRef.current.panTo(position)
    }, [position])

    const handleUseCurrent = () => {
        if (!navigator.geolocation) return
        navigator.geolocation.getCurrentPosition(
            pos => {
                setPosition({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                })
            },
            undefined
        )
    }

    const canSave = !!position

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            dialogClassName="geo-picker-modal"
            contentClassName="geo-picker-content"
        >
            <Modal.Header closeButton>
                <Modal.Title>Seleccionar ubicación</Modal.Title>
            </Modal.Header>
            <Modal.Body className="geo-picker-body">
                <div className="geo-picker-map">
                    {position && mapProvider === "osm" ? (
                        <LeafletGeoPickerMap position={position} onPositionChange={setPosition} />
                    ) : position ? (
                        <GoogleMapReact
                            bootstrapURLKeys={{ key: Settings.GoogleMapsKey }}
                            defaultCenter={position}
                            center={position}
                            defaultZoom={15}
                            onClick={handleMapClick}
                            yesIWantToUseGoogleMapApiInternals
                            onGoogleApiLoaded={handleApiLoaded}
                        />
                    ) : null}
                </div>
                <div className="geo-picker-info">
                    <div className="geo-picker-coords">
                        <span>Lat: {position?.lat ?? "-"}</span>
                        <span>Lng: {position?.lng ?? "-"}</span>
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={handleUseCurrent}>
                        <i className="fas fa-crosshairs me-2"></i>
                        Usar mi ubicación
                    </Button>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    disabled={!canSave}
                    onClick={() => {
                        if (!position) return
                        onSelect?.(position)
                        onClose()
                    }}
                >
                    Guardar ubicación
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default GeoPickerModal
